// A "···" button that shows a short list of actions (recipe page and
// cooking layout). A disclosure, not an ARIA menu, so ordinary Tab
// navigation works inside it. Closes on a choice, a click outside, or
// Escape (focus goes back to the button).
import { useEffect, useRef, useState } from "react";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS } from "../lib/constants";

export interface MoreMenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  // Keep the menu open after choosing (e.g. a two-step delete).
  keepOpen?: boolean;
}

// onClose runs whenever the menu closes, however it closes -- e.g. to undo
// the first step of a two-step delete.
export function MoreMenu({ label, items, onClose }: { label: string; items: MoreMenuItem[]; onClose?: () => void }) {
  const [open, setOpen] = useState(false);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) onClose?.();
    wasOpen.current = open;
  }, [open, onClose]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="mp-sans" style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={buttonRef}
        type="button"
        className="mp-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, borderRadius: RADIUS.pill, border: `1px solid ${COLORS.line}`, background: COLORS.card, color: COLORS.ink, fontSize: FONT_SIZE.base, fontWeight: 700, padding: 0 }}
      >
        ···
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + var(--space-md))",
            right: 0,
            zIndex: 40,
            minWidth: 220,
            maxWidth: "calc(100vw - 2 * var(--space-x3))",
            background: COLORS.card,
            border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.xl,
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
            padding: "var(--space-xs) var(--space-xxl)",
          }}
        >
          {items.map((item, i) => (
            <button
              key={item.label}
              type="button"
              className="mp-btn"
              onClick={() => {
                item.onSelect();
                if (!item.keepOpen) setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                minHeight: LAYOUT.tapTarget,
                padding: 0,
                background: "transparent",
                borderBottom: i === items.length - 1 ? "none" : `1px solid ${COLORS.line}`,
                color: item.danger ? COLORS.danger : COLORS.ink,
                fontSize: FONT_SIZE.base,
                fontWeight: item.danger ? 700 : 400,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
